import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  FaArrowLeft,
  FaEdit,
  FaSave,
  FaTimes,
  FaUser,
  FaFileContract,
  FaCalendarCheck,
  FaFileAlt,
  FaDollarSign,
  FaStickyNote,
  FaUsers,
  FaAddressBook,
  FaCreditCard,
  FaCalendarAlt,
  FaGavel,
  FaClipboardList,
  FaSearch,
  FaExclamationTriangle,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaBriefcase,
  FaIdCard,
  FaRuler,
  FaWeight,
  FaEye
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import AddressAutocomplete from '../components/AddressAutocomplete';
import './DefendantProfile.css';

const DefendantProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [defendant, setDefendant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [formData, setFormData] = useState({});

  // Navigation tabs configuration
  const tabs = [
    { id: 'profile', label: 'Profile', icon: FaUser },
    { id: 'bonds', label: 'Bonds', icon: FaFileContract },
    { id: 'checkins', label: 'Check-Ins', icon: FaCalendarCheck },
    { id: 'documents', label: 'Documents', icon: FaFileAlt },
    { id: 'payments', label: 'Payments', icon: FaDollarSign },
    { id: 'notes', label: 'Notes', icon: FaStickyNote },
    { id: 'indemnitors', label: 'Indemnitors', icon: FaUsers },
    { id: 'references', label: 'References', icon: FaAddressBook },
    { id: 'saved-cards', label: 'Saved Cards', icon: FaCreditCard },
    { id: 'appointments', label: 'Appointments', icon: FaCalendarAlt },
    { id: 'attorney', label: 'Attorney', icon: FaGavel },
    { id: 'logs', label: 'Logs', icon: FaClipboardList },
    { id: 'background', label: 'Background Checks', icon: FaSearch },
    { id: 'followups', label: 'Follow Ups', icon: FaExclamationTriangle },
    { id: 'warrants', label: 'Arrest Warrants', icon: FaExclamationTriangle }
  ];

  useEffect(() => {
    fetchDefendantDetails();
  }, [id]);

  const fetchDefendantDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/defendants/${id}`);
      if (response.data.success) {
        setDefendant(response.data.data);
        setFormData(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch defendant details:', error);
      toast.error('Failed to load defendant details');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleAddressSelect = (addressData) => {
    console.log('=== Address Select Called ===');
    console.log('Received address data:', addressData);
    console.log('Street:', addressData.street);
    console.log('City:', addressData.city);
    console.log('State:', addressData.state);
    console.log('ZIP:', addressData.zip);

    // Update form data state
    setFormData(prevFormData => {
      const newData = {
        ...prevFormData,
        address_street: addressData.street,
        address_city: addressData.city,
        address_state: addressData.state,
        address_zip: addressData.zip
      };
      console.log('New formData will be:', newData);
      return newData;
    });

    // Show what we're setting
    toast.info(`Setting: ${addressData.street} | ${addressData.city}, ${addressData.state} ${addressData.zip}`, {
      autoClose: 5000
    });
  };

  const handleSave = async () => {
    try {
      const updateData = {
        firstName: formData.first_name,
        middleName: formData.middle_name,
        lastName: formData.last_name,
        maidenName: formData.maiden_name,
        alias: formData.alias,
        suffix: formData.suffix,
        dateOfBirth: formData.date_of_birth,
        ssn: formData.ssn,
        citizenship: formData.citizenship,
        placeOfBirth: formData.place_of_birth,
        dateOfArrest: formData.date_of_arrest,
        dateOfRelease: formData.date_of_release,
        driversLicense: formData.drivers_license,
        driversLicenseState: formData.drivers_license_state,
        phonePrimary: formData.phone_primary,
        phoneSecondary: formData.phone_secondary,
        email: formData.email,
        addressStreet: formData.address_street,
        addressCity: formData.address_city,
        addressState: formData.address_state,
        addressZip: formData.address_zip,
        height: formData.height,
        weight: formData.weight,
        eyeColor: formData.eye_color,
        hairColor: formData.hair_color,
        gender: formData.gender,
        race: formData.race,
        occupation: formData.occupation,
        employer: formData.employer,
        employerPhone: formData.employer_phone,
        clientStatus: formData.client_status,
        inCustody: formData.in_custody,
        currentCaseNumber: formData.current_case_number,
        postedDate: formData.posted_date
      };

      const response = await axios.put(`/defendants/${id}`, updateData);
      if (response.data.success) {
        setDefendant(response.data.data);
        setEditMode(false);
        toast.success('Defendant updated successfully');
        fetchDefendantDetails();
      }
    } catch (error) {
      console.error('Failed to update defendant:', error);
      toast.error('Failed to update defendant');
    }
  };

  const handleCancel = () => {
    setFormData(defendant);
    setEditMode(false);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="profile-loading">
        <div className="spinner"></div>
        <p>Loading defendant profile...</p>
      </div>
    );
  }

  if (!defendant) {
    return (
      <div className="profile-error">
        <FaExclamationTriangle />
        <h2>Defendant Not Found</h2>
        <button className="btn btn-primary" onClick={() => navigate('/defendants')}>
          Back to Defendants
        </button>
      </div>
    );
  }

  return (
    <div className="defendant-profile">
      {/* Header */}
      <div className="profile-header">
        <div className="header-left">
          <button className="btn-back" onClick={() => navigate('/defendants')}>
            <FaArrowLeft /> Back to Defendants
          </button>
          <h1>
            {defendant.first_name} {defendant.middle_name} {defendant.last_name}
          </h1>
        </div>
        <div className="header-actions">
          {!editMode ? (
            <button className="btn btn-primary" onClick={() => setEditMode(true)}>
              <FaEdit /> Edit Profile
            </button>
          ) : (
            <>
              <button className="btn btn-success" onClick={handleSave}>
                <FaSave /> Save Changes
              </button>
              <button className="btn btn-secondary" onClick={handleCancel}>
                <FaTimes /> Cancel
              </button>
            </>
          )}
        </div>
      </div>

      {/* Profile Summary Card */}
      <div className="profile-summary-card">
        <div className="summary-left">
          <div className="defendant-photo">
            {defendant.photo_url ? (
              <img src={defendant.photo_url} alt={`${defendant.first_name} ${defendant.last_name}`} />
            ) : (
              <div className="photo-placeholder">
                <FaUser />
              </div>
            )}
          </div>
          <div className="quick-info">
            <div className={`info-item ${!defendant.phone_primary ? 'field-missing-info' : ''}`}>
              <FaPhone />
              <span>{defendant.phone_primary || '-'}</span>
            </div>
            <div className={`info-item ${!defendant.email ? 'field-missing-info' : ''}`}>
              <FaEnvelope />
              <span>{defendant.email || '-'}</span>
            </div>
            <div className={`info-item ${!defendant.ssn ? 'field-missing-info' : ''}`}>
              <FaIdCard />
              <span>SSN: {defendant.ssn || '-'}</span>
            </div>
            <div className={`info-item ${!defendant.date_of_birth ? 'field-missing-info' : ''}`}>
              <FaCalendarAlt />
              <span>DOB: {formatDate(defendant.date_of_birth)}</span>
            </div>
            <div className={`info-item ${!defendant.address_street || !defendant.address_city || !defendant.address_state ? 'field-missing-info' : ''}`}>
              <FaMapMarkerAlt />
              <span>
                {defendant.address_street && defendant.address_city && defendant.address_state
                  ? `${defendant.address_street}${defendant.address_unit ? ' ' + defendant.address_unit : ''}, ${defendant.address_city}, ${defendant.address_state} ${defendant.address_zip || ''}`
                  : defendant.address_city && defendant.address_state
                  ? `${defendant.address_city}, ${defendant.address_state}`
                  : '-'}
              </span>
            </div>
          </div>
        </div>
        <div className="summary-stats">
          <div className="stat-box">
            <div className="stat-value">{defendant.bonds?.length || 0}</div>
            <div className="stat-label">Total Bonds</div>
          </div>
          <div className="stat-box">
            <div className="stat-value">{defendant.active_bond_count || 0}</div>
            <div className="stat-label">Active Bonds</div>
          </div>
          <div className="stat-box">
            <div className="stat-value">
              {formatCurrency(
                defendant.bonds?.reduce((sum, bond) => sum + (parseFloat(bond.total_amount) || 0), 0)
              )}
            </div>
            <div className="stat-label">Total Bond Amount</div>
          </div>
          <div className="stat-box">
            <div className="stat-value">{defendant.recent_checkins?.length || 0}</div>
            <div className="stat-label">Recent Check-ins</div>
          </div>
          <div className="stat-box">
            <div className="stat-value">
              {(() => {
                const upcomingCourtDates = defendant.court_dates?.filter(
                  cd => new Date(cd.scheduled_date) >= new Date() && cd.status === 'scheduled'
                ).sort((a, b) => new Date(a.scheduled_date) - new Date(b.scheduled_date));
                return upcomingCourtDates && upcomingCourtDates.length > 0
                  ? formatDate(upcomingCourtDates[0].scheduled_date)
                  : '-';
              })()}
            </div>
            <div className="stat-label">Next Court Date</div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="profile-content">
        {/* Left Sidebar Navigation */}
        <div className="profile-sidebar">
          <div className="sidebar-tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`sidebar-tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <tab.icon />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Right Content Panel */}
        <div className="profile-panel">
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="tab-content">
              <h2>Personal Information</h2>

              {editMode ? (
                // Edit Mode - Show Form Fields
                <div className="info-sections">
                  {/* Basic Information */}
                  <div className="info-section">
                    <h3>Basic Information</h3>
                    <div className="info-grid">
                      <div className="info-field">
                        <label>First Name</label>
                        <input
                          type="text"
                          name="first_name"
                          value={formData.first_name || ''}
                          onChange={handleInputChange}
                          className="form-control"
                        />
                      </div>

                      <div className="info-field">
                        <label>Middle Name</label>
                        <input
                          type="text"
                          name="middle_name"
                          value={formData.middle_name || ''}
                          onChange={handleInputChange}
                          className="form-control"
                        />
                      </div>

                      <div className="info-field">
                        <label>Last Name</label>
                        <input
                          type="text"
                          name="last_name"
                          value={formData.last_name || ''}
                          onChange={handleInputChange}
                          className="form-control"
                        />
                      </div>

                      <div className="info-field">
                        <label>Date of Birth</label>
                        <input
                          type="date"
                          name="date_of_birth"
                          value={formData.date_of_birth?.split('T')[0] || ''}
                          onChange={handleInputChange}
                          className={`form-control ${!formData.date_of_birth ? 'field-missing' : ''}`}
                        />
                      </div>

                      <div className="info-field">
                        <label>Gender</label>
                        <select
                          name="gender"
                          value={formData.gender || ''}
                          onChange={handleInputChange}
                          className="form-control"
                        >
                          <option value="">Select...</option>
                          <option value="male">Male</option>
                          <option value="female">Female</option>
                          <option value="other">Other</option>
                        </select>
                      </div>

                      <div className="info-field">
                        <label>Race</label>
                        <select
                          name="race"
                          value={formData.race || ''}
                          onChange={handleInputChange}
                          className={`form-control ${!formData.race ? 'field-missing' : ''}`}
                        >
                          <option value="">Select Race</option>
                          <option value="Asian">Asian</option>
                          <option value="Black">Black</option>
                          <option value="Hispanic">Hispanic</option>
                          <option value="Native American">Native American</option>
                          <option value="White">White</option>
                          <option value="Unknown">Unknown</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Physical Description */}
                  <div className="info-section">
                    <h3>Physical Description</h3>
                    <div className="info-grid">
                      <div className="info-field">
                        <label>Height</label>
                        <input
                          type="text"
                          name="height"
                          value={formData.height || ''}
                          onChange={handleInputChange}
                          className="form-control"
                          placeholder="e.g., 5'10&quot;"
                        />
                      </div>

                      <div className="info-field">
                        <label>Weight</label>
                        <input
                          type="text"
                          name="weight"
                          value={formData.weight || ''}
                          onChange={handleInputChange}
                          className="form-control"
                          placeholder="e.g., 180 lbs"
                        />
                      </div>

                      <div className="info-field">
                        <label>Eye Color</label>
                        <input
                          type="text"
                          name="eye_color"
                          value={formData.eye_color || ''}
                          onChange={handleInputChange}
                          className="form-control"
                        />
                      </div>

                      <div className="info-field">
                        <label>Hair Color</label>
                        <input
                          type="text"
                          name="hair_color"
                          value={formData.hair_color || ''}
                          onChange={handleInputChange}
                          className="form-control"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div className="info-section">
                    <h3>Contact Information</h3>
                    <div className="info-grid">
                      <div className="info-field">
                        <label>Primary Phone</label>
                        <input
                          type="tel"
                          name="phone_primary"
                          value={formData.phone_primary || ''}
                          onChange={handleInputChange}
                          className={`form-control ${!formData.phone_primary ? 'field-missing' : ''}`}
                        />
                      </div>

                      <div className="info-field">
                        <label>Secondary Phone</label>
                        <input
                          type="tel"
                          name="phone_secondary"
                          value={formData.phone_secondary || ''}
                          onChange={handleInputChange}
                          className="form-control"
                        />
                      </div>

                      <div className="info-field full-width">
                        <label>Email</label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email || ''}
                          onChange={handleInputChange}
                          className={`form-control ${!formData.email ? 'field-missing' : ''}`}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Address */}
                  <div className="info-section">
                    <h3>Address</h3>
                    <div className="info-grid">
                      <div className="info-field full-width">
                        <label>Street Address</label>
                        <AddressAutocomplete
                          value={formData.address_street || ''}
                          onAddressSelect={handleAddressSelect}
                          placeholder="Start typing address..."
                          className={`form-control ${!formData.address_street ? 'field-missing' : ''}`}
                        />
                      </div>

                      <div className="info-field">
                        <label>Apartment / Unit / Block</label>
                        <input
                          type="text"
                          name="address_unit"
                          value={formData.address_unit || ''}
                          onChange={handleInputChange}
                          className="form-control"
                          placeholder="Apt, Unit, Suite, etc."
                        />
                      </div>

                      <div className="info-field">
                        <label>City</label>
                        <input
                          type="text"
                          name="address_city"
                          value={formData.address_city || ''}
                          onChange={handleInputChange}
                          className={`form-control ${!formData.address_city ? 'field-missing' : ''}`}
                        />
                      </div>

                      <div className="info-field">
                        <label>State</label>
                        <input
                          type="text"
                          name="address_state"
                          value={formData.address_state || ''}
                          onChange={handleInputChange}
                          className={`form-control ${!formData.address_state ? 'field-missing' : ''}`}
                        />
                      </div>

                      <div className="info-field">
                        <label>ZIP Code</label>
                        <input
                          type="text"
                          name="address_zip"
                          value={formData.address_zip || ''}
                          onChange={handleInputChange}
                          className="form-control"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Identification */}
                  <div className="info-section">
                    <h3>Identification</h3>
                    <div className="info-grid">
                      <div className="info-field">
                        <label>SSN</label>
                        <input
                          type="text"
                          name="ssn"
                          value={formData.ssn || ''}
                          onChange={handleInputChange}
                          className={`form-control ${!formData.ssn ? 'field-missing' : ''}`}
                          placeholder="XXX-XX-XXXX"
                        />
                      </div>

                      <div className="info-field">
                        <label>Driver's License</label>
                        <input
                          type="text"
                          name="drivers_license"
                          value={formData.drivers_license || ''}
                          onChange={handleInputChange}
                          className="form-control"
                        />
                      </div>

                      <div className="info-field">
                        <label>DL State</label>
                        <input
                          type="text"
                          name="drivers_license_state"
                          value={formData.drivers_license_state || ''}
                          onChange={handleInputChange}
                          className="form-control"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Employment */}
                  <div className="info-section">
                    <h3>Employment Information</h3>
                    <div className="info-grid">
                      <div className="info-field">
                        <label>Occupation</label>
                        <input
                          type="text"
                          name="occupation"
                          value={formData.occupation || ''}
                          onChange={handleInputChange}
                          className="form-control"
                        />
                      </div>

                      <div className="info-field">
                        <label>Employer</label>
                        <input
                          type="text"
                          name="employer"
                          value={formData.employer || ''}
                          onChange={handleInputChange}
                          className="form-control"
                        />
                      </div>

                      <div className="info-field">
                        <label>Employer Phone</label>
                        <input
                          type="tel"
                          name="employer_phone"
                          value={formData.employer_phone || ''}
                          onChange={handleInputChange}
                          className="form-control"
                        />
                      </div>
                    </div>
                  </div>

                </div>
              ) : (
                // View Mode - Condensed Inline Display
                <div className="condensed-info">
                  {/* Personal Information */}
                  <div className="info-line">
                    <span className="info-label">FIRST NAME:</span> <span className="info-value">{defendant.first_name}</span>
                    {defendant.middle_name && <><span className="info-label">MIDDLE NAME:</span> <span className="info-value">{defendant.middle_name}</span></>}
                    <span className="info-label">LAST NAME:</span> <span className="info-value">{defendant.last_name}</span>
                    {defendant.maiden_name && <><span className="info-label">MAIDEN NAME:</span> <span className="info-value">{defendant.maiden_name}</span></>}
                    {defendant.suffix && <><span className="info-label">SUFFIX:</span> <span className="info-value">{defendant.suffix}</span></>}
                  </div>

                  {defendant.alias && (
                    <div className="info-line">
                      <span className="info-label">ALIAS:</span> <span className="info-value">{defendant.alias}</span>
                    </div>
                  )}

                  <div className="info-line">
                    <span className="info-label">DOB:</span> <span className="info-value">{formatDate(defendant.date_of_birth).toUpperCase()}</span>
                    <span className="info-label">GENDER:</span> <span className="info-value">{defendant.gender?.toUpperCase() || '-'}</span>
                    <span className="info-label">RACE:</span> <span className="info-value">{defendant.race?.toUpperCase() || '-'}</span>
                  </div>

                  <div className="info-line">
                    <span className="info-label">CITIZENSHIP:</span> <span className="info-value">{defendant.citizenship || 'USA'}</span>
                    {defendant.place_of_birth && <><span className="info-label">PLACE OF BIRTH:</span> <span className="info-value">{defendant.place_of_birth}</span></>}
                  </div>

                  {/* Physical Description */}
                  <div className="info-line">
                    {defendant.height && <><span className="info-label">HEIGHT:</span> <span className="info-value">{defendant.height}</span></>}
                    {defendant.weight && <><span className="info-label">WEIGHT:</span> <span className="info-value">{defendant.weight}</span></>}
                    {defendant.eye_color && <><span className="info-label">EYE COLOR:</span> <span className="info-value">{defendant.eye_color.toUpperCase()}</span></>}
                    {defendant.hair_color && <><span className="info-label">HAIR COLOR:</span> <span className="info-value">{defendant.hair_color.toUpperCase()}</span></>}
                  </div>

                  {/* Contact Information */}
                  <div className="info-line">
                    <span className="info-label">EMAIL:</span> <span className="info-value">{defendant.email || '-'}</span>
                  </div>

                  <div className="info-line">
                    <span className="info-label">PRIMARY PHONE:</span> <span className="info-value">{defendant.phone_primary || '-'}</span>
                    {defendant.phone_secondary && <><span className="info-label">SECONDARY PHONE:</span> <span className="info-value">{defendant.phone_secondary}</span></>}
                  </div>

                  {/* Address */}
                  <div className="info-line">
                    <span className="info-label">ADDRESS:</span> <span className="info-value">
                      {defendant.address_street || '-'}
                      {defendant.address_city && `, ${defendant.address_city}`}
                      {defendant.address_state && `, ${defendant.address_state}`}
                      {defendant.address_zip && ` ${defendant.address_zip}`}
                    </span>
                  </div>

                  {/* Identification */}
                  <div className="info-line">
                    <span className="info-label">SSN:</span> <span className="info-value">{defendant.ssn || '-'}</span>
                    {defendant.drivers_license && <><span className="info-label">DRIVER'S LICENSE:</span> <span className="info-value">{defendant.drivers_license}</span></>}
                    {defendant.drivers_license_state && <><span className="info-label">DL STATE:</span> <span className="info-value">{defendant.drivers_license_state}</span></>}
                  </div>

                  {/* Arrest/Release Information */}
                  <div className="section-divider">ARREST & CUSTODY INFORMATION</div>
                  <div className="info-line">
                    {defendant.date_of_arrest && <><span className="info-label">DATE OF ARREST:</span> <span className="info-value">{formatDate(defendant.date_of_arrest)}</span></>}
                    {defendant.date_of_release && <><span className="info-label">DATE OF RELEASE:</span> <span className="info-value">{formatDate(defendant.date_of_release)}</span></>}
                    {defendant.posted_date && <><span className="info-label">POSTED DATE:</span> <span className="info-value">{formatDate(defendant.posted_date)}</span></>}
                  </div>

                  <div className="info-line">
                    <span className="info-label">IN CUSTODY:</span> <span className="info-value">{defendant.in_custody ? 'YES' : 'NO'}</span>
                    <span className="info-label">CLIENT STATUS:</span> <span className="info-value">{defendant.client_status?.toUpperCase() || 'ACTIVE'}</span>
                    {defendant.current_case_number && <><span className="info-label">CASE NUMBER:</span> <span className="info-value">{defendant.current_case_number}</span></>}
                  </div>

                  {/* Jail Information */}
                  {(defendant.current_jail_name || defendant.last_jail_name) && (
                    <div className="info-line">
                      {defendant.current_jail_name && <><span className="info-label">CURRENT JAIL:</span> <span className="info-value">{defendant.current_jail_name}</span></>}
                      {defendant.last_jail_name && <><span className="info-label">LAST JAIL:</span> <span className="info-value">{defendant.last_jail_name}</span></>}
                    </div>
                  )}

                  {/* Jail History */}
                  {defendant.jail_history && defendant.jail_history.length > 0 && (
                    <div className="emergency-contacts-condensed">
                      <div className="section-divider">JAIL HISTORY</div>
                      {defendant.jail_history.map((history, index) => (
                        <div key={index} className="info-line">
                          <span className="info-label">JAIL:</span> <span className="info-value">{history.jail_name || '-'}</span>
                          <span className="info-label">ADMITTED:</span> <span className="info-value">{formatDate(history.admission_date)}</span>
                          {history.release_date && <><span className="info-label">RELEASED:</span> <span className="info-value">{formatDate(history.release_date)}</span></>}
                          {history.reason && <><span className="info-label">REASON:</span> <span className="info-value">{history.reason}</span></>}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Employment */}
                  {(defendant.occupation || defendant.employer || defendant.employer_phone) && (
                    <>
                      <div className="section-divider">EMPLOYMENT</div>
                      <div className="info-line">
                        {defendant.occupation && <><span className="info-label">OCCUPATION:</span> <span className="info-value">{defendant.occupation}</span></>}
                        {defendant.employer && <><span className="info-label">EMPLOYER:</span> <span className="info-value">{defendant.employer}</span></>}
                        {defendant.employer_phone && <><span className="info-label">EMPLOYER PHONE:</span> <span className="info-value">{defendant.employer_phone}</span></>}
                      </div>
                    </>
                  )}

                  {/* System Information */}
                  <div className="section-divider">SYSTEM INFORMATION</div>
                  <div className="info-line">
                    <span className="info-label">ADDED BY:</span> <span className="info-value">{defendant.created_by_name || '-'}</span>
                    <span className="info-label">CREATED DATE:</span> <span className="info-value">{formatDate(defendant.created_at)}</span>
                  </div>

                  {/* Staff Notes */}
                  {defendant.notes && defendant.notes.length > 0 && (
                    <>
                      <div className="section-divider">STAFF NOTES</div>
                      {defendant.notes.map((note, index) => (
                        <div key={index} className="info-line" style={{flexDirection: 'column', alignItems: 'flex-start', gap: '8px'}}>
                          <div style={{display: 'flex', gap: '12px', width: '100%', flexWrap: 'wrap'}}>
                            <span className="info-label">{formatDateTime(note.created_at)}</span>
                            <span className="info-label">{note.created_by_name || 'Unknown'}</span>
                            {note.note_type !== 'general' && <span className="info-label" style={{background: note.note_type === 'important' ? '#dc3545' : note.note_type === 'warning' ? '#ffc107' : '#17a2b8'}}>{note.note_type.toUpperCase()}</span>}
                          </div>
                          <span className="info-value" style={{width: '100%', whiteSpace: 'pre-wrap'}}>{note.note_text}</span>
                        </div>
                      ))}
                    </>
                  )}

                  {/* Emergency Contacts */}
                  {defendant.emergency_contacts && defendant.emergency_contacts.length > 0 && (
                    <div className="emergency-contacts-condensed">
                      <div className="section-divider">EMERGENCY CONTACTS</div>
                      {defendant.emergency_contacts.map((contact, index) => (
                        <div key={index} className="info-line">
                          <span className="info-label">NAME:</span> <span className="info-value">{contact.name}</span>
                          <span className="info-label">RELATIONSHIP:</span> <span className="info-value">{contact.relationship}</span>
                          <span className="info-label">PHONE:</span> <span className="info-value">{contact.phone_primary}</span>
                          {contact.email && <><span className="info-label">EMAIL:</span> <span className="info-value">{contact.email}</span></>}
                          {contact.is_primary && <span className="primary-badge">PRIMARY</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Bonds Tab */}
          {activeTab === 'bonds' && (
            <div className="tab-content">
              <h2>Bonds</h2>
              {defendant.bonds && defendant.bonds.length > 0 ? (
                <div className="bonds-list">
                  {defendant.bonds.map((bond) => (
                    <div key={bond.id} className="bond-card">
                      <div className="bond-header">
                        <div>
                          <h3>Power #{bond.bond_number}</h3>
                          <span className={`badge badge-${bond.status}`}>{bond.status}</span>
                        </div>
                        <div className="bond-amount">{formatCurrency(bond.total_amount)}</div>
                      </div>
                      <div className="bond-details">
                        <div className="detail-row">
                          <label>Court:</label>
                          <span>{bond.court_name || '-'}</span>
                        </div>
                        <div className="detail-row">
                          <label>Agent:</label>
                          <span>{bond.agent_name || '-'}</span>
                        </div>
                        <div className="detail-row">
                          <label>Issue Date:</label>
                          <span>{formatDate(bond.issue_date)}</span>
                        </div>
                        {bond.charge && (
                          <div className="detail-row">
                            <label>Charge:</label>
                            <span>{bond.charge}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <FaFileContract />
                  <p>No bonds found for this defendant</p>
                </div>
              )}
            </div>
          )}

          {/* Check-ins Tab */}
          {activeTab === 'checkins' && (
            <div className="tab-content">
              <h2>Recent Check-Ins</h2>
              {defendant.recent_checkins && defendant.recent_checkins.length > 0 ? (
                <div className="checkins-list">
                  {defendant.recent_checkins.map((checkin, index) => (
                    <div key={index} className="checkin-card">
                      <div className="checkin-time">
                        <FaCalendarCheck />
                        <span>{formatDateTime(checkin.checkin_time)}</span>
                      </div>
                      {checkin.notes && <div className="checkin-notes">{checkin.notes}</div>}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <FaCalendarCheck />
                  <p>No check-ins recorded</p>
                </div>
              )}
            </div>
          )}

          {/* Placeholder tabs for future development */}
          {['documents', 'payments', 'notes', 'indemnitors', 'references', 'saved-cards', 'appointments', 'attorney', 'logs', 'background', 'followups', 'warrants'].includes(activeTab) && (
            <div className="tab-content">
              <h2>{tabs.find((t) => t.id === activeTab)?.label}</h2>
              <div className="empty-state">
                <FaFileAlt />
                <p>This module is under development</p>
                <small>Coming soon...</small>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DefendantProfile;
